// @flow
import { GK_TYPE, BUNDLE_TYPE } from '@site-builder/common/src/utils/store';
import React from 'react';

import type { CustomButtonStyles } from '../CallToAction';
import type { Sku } from '@site-builder/common/src/flow-types/sku';
import type { CustomButtonComponent } from '@site-builder/common/src/types/block/common/custom-button-component';
import type { RenderMode } from '@site-builder/common/src/types/build';
import type { FiveSymbolLocale } from '@site-builder/common/src/types/locale';
import type { Subscription } from '@site-builder/common/src/types/model/subscription';
import type { StoreItem } from '@site-builder/common/src/types/store';

import { addScriptData } from '../../../reducers/modules/site-builder/scripts';
import AppStore from './Buttons/AppStore';
import BundleButton from './Buttons/BundleButton';
import { WithCartButton } from './Buttons/CartButton';
import GameKeysButton from './Buttons/GameKeysButton';
import GooglePlay from './Buttons/GooglePlay';
import LinkButton from './Buttons/LinkButton';
import SubscriptionButton from './Buttons/SubscriptionButton';


type Props = {|
  button: CustomButtonComponent,
  onClick: () => void,
  onChangeText: (e: Event) => void,
  onBlur: () => void,
  buttonId: string,
  blockType: string,
  skus: Sku[],
  bundles: StoreItem[],
  isStoreDataLoading: boolean,
  subscriptionPlans: Subscription[],
  isSubscriptionLoading: boolean,
  merchantId: string,
  projectId: string,
  cartId: string,
  analyticsCounterId: number,
  isNewScheme: boolean,
  isMultipage: boolean,
  enableCart: boolean,
  customStyles: CustomButtonStyles,
  locale: FiveSymbolLocale,
  renderMode: RenderMode,
  isServerSideRender: boolean,
  addScriptData: typeof addScriptData,
|};

const CustomButton = ({
  button,
  onClick,
  blockType,
  skus,
  bundles,
  isStoreDataLoading,
  subscriptionPlans,
  isSubscriptionLoading,
  merchantId,
  projectId,
  cartId,
  analyticsCounterId,
  isNewScheme,
  isMultipage,
  onChangeText,
  onBlur,
  buttonId,
  customStyles,
  locale,
  renderMode,
  isServerSideRender,
  addScriptData,
  enableCart
}: Props) => {
  switch (button.subtype) {
    case 'buy': {
      switch (button.value.buy.type) {
        case 'key': {
          return (
            <WithCartButton
              sku={button.value.buy.id}
              itemType={GK_TYPE}
              cartId={cartId}
              projectId={projectId}
              locale={locale}
              isMultipage={isMultipage}
              customStyles={customStyles}
              isNewScheme={isNewScheme}
              enableCart={enableCart}
            >
              <GameKeysButton
                button={button}
                buttonId={buttonId}
                blockType={blockType}
                skus={skus}
                isStoreDataLoading={isStoreDataLoading}
                projectId={projectId}
                analyticsCounterId={analyticsCounterId}
                isNewScheme={isNewScheme}
                onClick={onClick}
                locale={locale}
                theme={customStyles}
                renderMode={renderMode}
                isServerSideRender={isServerSideRender}
              />
            </WithCartButton>
          );
        }
        case 'subscription': {
          return (
            <SubscriptionButton
              button={button}
              buttonId={buttonId}
              blockType={blockType}
              subscriptionPlans={subscriptionPlans}
              isSubscriptionLoading={isSubscriptionLoading}
              onClick={onClick}
              locale={locale}
              theme={customStyles}
              isServerSideRender={isServerSideRender}
              addScriptData={addScriptData}
            />
          );
        }
        case 'bundle': {
          return (
            <WithCartButton
              sku={button.value.buy.id}
              itemType={BUNDLE_TYPE}
              cartId={cartId}
              projectId={projectId}
              locale={locale}
              isMultipage={isMultipage}
              customStyles={customStyles}
              isNewScheme={isNewScheme}
              enableCart={enableCart}
            >
              <BundleButton
                button={button}
                buttonId={buttonId}
                blockType={blockType}
                bundles={bundles}
                isStoreDataLoading={isStoreDataLoading}
                projectId={projectId}
                analyticsCounterId={analyticsCounterId}
                isNewScheme={isNewScheme}
                onClick={onClick}
                locale={locale}
                theme={customStyles}
                renderMode={renderMode}
                isServerSideRender={isServerSideRender}
              />
            </WithCartButton>
          );
        }
        default: return null;
      }
    }
    case 'link': {
      return (
        <LinkButton
          button={button}
          onChangeText={onChangeText}
          blockType={blockType}
          onBlur={onBlur}
          onClick={onClick}
          theme={customStyles}
          locale={locale}
          isServerSideRender={isServerSideRender}
        />
      );
    }
    case 'preset': {
      switch (button.value[button.subtype].type) {
        case 'google_play': {
          return (
            <GooglePlay
              value={button.value[button.subtype].link}
              blockType={blockType}
              merchantId={merchantId}
              onClick={onClick}
              isServerSideRender={isServerSideRender}
            />
          );
        }
        case 'app_store': {
          return (
            <AppStore
              value={button.value[button.subtype].link}
              blockType={blockType}
              merchantId={merchantId}
              onClick={onClick}
              isServerSideRender={isServerSideRender}
            />
          );
        }
        default: return null;
      }
    }
    default: return null;
  }
};

export default CustomButton;
